/**
 * `object_card` — one place: photographs, what it is, whether it is open.
 *
 * The badge is `status` + `status_note`, and the note is the museum's OWN words
 * for a closure, quoted. Neither side composes that sentence: it is what keeps
 * the grammar honest as well as the fact («закрыт» agrees with «Марли» and
 * would have to be «закрыта» for «Ева»).
 */

import { useState } from 'react';
import type { ObjectCardProps } from '../generated/schemas';
import { cn } from '../../lib/cn';
import { stripEmoji as plain } from '../../lib/emoji';
import { hasNoBoard, useSession } from '../../store/sessionStore';
import { useInlineCard } from '../inlineCard';
import CardActions from './CardActions';

const MAX_IMAGES = 6;
/** A horizontal swipe past this many pixels turns the gallery (#153, the Agent's chat). */
export const SWIPE_PX = 40;

/** The photo shown after a step of *delta* (±1) from *active* among *count* — round. */
export function turnGallery(active: number, delta: number, count: number): number {
  if (count <= 0) return 0;
  return (((active + delta) % count) + count) % count;
}

const STATUS: Record<string, { label: string; tone: string }> = {
  open: { label: 'открыто', tone: 'open' },
  closed: { label: 'закрыто', tone: 'closed' },
  dayoff: { label: 'выходной', tone: 'closed' },
  restoration: { label: 'на реставрации', tone: 'restoration' },
};

/**
 * The board-less Agent's chat draws a place as the Figma «Paragraph» (3-1
 * «Chat - MainInfo», 07.10.2026): the name, the facts, the museum's sentence,
 * and the photographs side by side in a row the finger scrolls — no card
 * frame, no arrows. Same props, same badge and quoted note, same actions.
 */
function ObjectParagraph({ props }: { props: ObjectCardProps }) {
  const [broken, setBroken] = useState<Set<string>>(() => new Set());
  const images = (props.images ?? []).slice(0, MAX_IMAGES).filter((image) => !broken.has(image));
  const badge = props.status ? STATUS[props.status] : undefined;
  return (
    <article className="card card--object card--paragraph">
      <header className="paragraph__head">
        <h3 className="paragraph__title">{plain(props.title)}</h3>
        {badge ? <span className={cn('badge', `badge--${badge.tone}`)}>{badge.label}</span> : null}
      </header>
      {props.subtitle ? <p className="card__meta paragraph__meta">{plain(props.subtitle)}</p> : null}
      {props.status_note ? <p className="notice notice--warn">{plain(props.status_note)}</p> : null}
      {props.text ? <p className="paragraph__text">{plain(props.text)}</p> : null}
      {images.length > 0 ? (
        <div className="photo-row" aria-label={`Фото: ${plain(props.title)}`}>
          {images.map((image) => (
            <img
              key={image}
              className="photo-row__image"
              src={image}
              alt=""
              loading="lazy"
              onError={() => setBroken((seen) => new Set(seen).add(image))}
            />
          ))}
        </div>
      ) : null}
      <CardActions sourceId={props.id} actions={props.actions ?? []} links={props.links ?? []} />
    </article>
  );
}

export default function ObjectCard({ props }: { props: ObjectCardProps }) {
  const inline = useInlineCard();
  const boardless = useSession((state) => hasNoBoard(state.assistantId));
  if (inline && boardless) return <ObjectParagraph props={props} />;
  return <ObjectGallery props={props} />;
}

function ObjectGallery({ props }: { props: ObjectCardProps }) {
  const [broken, setBroken] = useState<Set<number>>(() => new Set());
  const [active, setActive] = useState(0);
  const [touch, setTouch] = useState<{ x: number; y: number } | null>(null);
  // Optional on the wire: absent means absent, so the default lives here.
  const images = (props.images ?? []).slice(0, MAX_IMAGES).filter((_, index) => !broken.has(index));
  const badge = props.status ? STATUS[props.status] : undefined;
  const actions = props.actions ?? [];
  const links = props.links ?? [];

  return (
    <article className="card card--object">
      {images.length > 0 ? (
        <div
          className="gallery"
          // A carousel on a phone (#153): a swipe turns it, the arrows on a desktop.
          onTouchStart={(event) => {
            const t = event.touches[0];
            setTouch(t ? { x: t.clientX, y: t.clientY } : null);
          }}
          onTouchEnd={(event) => {
            const t = event.changedTouches[0];
            // a horizontal swipe only — scrolling the chat is vertical (review of !606)
            if (touch && t) {
              const dx = t.clientX - touch.x;
              if (Math.abs(dx) > SWIPE_PX && Math.abs(dx) > Math.abs(t.clientY - touch.y)) {
                setActive((index) => turnGallery(index, dx < 0 ? 1 : -1, images.length));
              }
            }
            setTouch(null);
          }}
        >
          <img
            className="gallery__image"
            src={images[Math.min(active, images.length - 1)]}
            alt=""
            loading="lazy"
            // A photograph that does not load leaves the card intact — the CMS
            // is the museum's and its URLs outlive our knowledge of them.
            onError={() =>
              setBroken((seen) => {
                const next = new Set(seen);
                next.add(active);
                return next;
              })
            }
          />
          {images.length > 1 ? (
            <>
              <button
                type="button"
                className="gallery__arrow gallery__arrow--prev"
                aria-label="Предыдущее фото"
                onClick={() => setActive((index) => turnGallery(index, -1, images.length))}
              >
                ‹
              </button>
              <button
                type="button"
                className="gallery__arrow gallery__arrow--next"
                aria-label="Следующее фото"
                onClick={() => setActive((index) => turnGallery(index, 1, images.length))}
              >
                ›
              </button>
            </>
          ) : null}
          {images.length > 1 ? (
            <div className="gallery__dots">
              {images.map((image, index) => (
                <button
                  key={image}
                  type="button"
                  className={cn('gallery__dot', index === active && 'gallery__dot--on')}
                  aria-label={`Фото ${index + 1}`}
                  aria-current={index === active}
                  onClick={() => setActive(index)}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="card__pad">
        <header className="card__head">
          <h3 className="card__title">{plain(props.title)}</h3>
          {badge ? <span className={cn('badge', `badge--${badge.tone}`)}>{badge.label}</span> : null}
        </header>
        {props.subtitle ? <p className="card__meta">{plain(props.subtitle)}</p> : null}
        {/* Quoted, never composed — the museum's own sentence about the closure. */}
        {props.status_note ? <p className="notice notice--warn">{plain(props.status_note)}</p> : null}
        {props.text ? <p className="card__text">{plain(props.text)}</p> : null}

        {/* Through the shared bridge now (§11.1/§11.4): a tap on this card sets
            `context.a2ui_action` exactly like a chip does, and there is no
            fallback to `label` — `value` is required by the schema. */}
        <CardActions sourceId={props.id} actions={actions} links={links} />
      </div>
    </article>
  );
}
